import {
  Box,
  Alert,
  Card,
  CardActionArea,
  Chip,
  Container,
  LinearProgress,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  Tooltip,
  Typography,
  type Theme,
} from "@mui/material";
import GridViewIcon from "@mui/icons-material/GridView";
import TableRowsIcon from "@mui/icons-material/TableRows";
import ImageNotSupportedIcon from "@mui/icons-material/ImageNotSupported";
import { Table, useTable } from "@dmca/frontend/components/table";
import { DateZone, luxonDTValiType, DateTime } from "@dmca/utils/luxon";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { number, object, optional, string } from "valibot";
import { PageHeader } from "@dmca/frontend/components/nav";
import {
  getKanbanStockStatus,
  getKanbanExpectedDate,
  isKanbanOrderOverdue,
  getKanbanRequestCategories,
  KanbanRequestCategoryNames,
  type IKanbanWithOrder,
} from "@dmca/models/interface/kanban";
import {
  UpdateKanbanModal,
  CreateKanbanModal,
} from "@dmca/frontend/widgets/kanban";
import { Button } from "@dmca/frontend/components/button";
import { Input, Select } from "@dmca/frontend/components/input";
import { useTRPCClient } from "@dmca/frontend/trpc";
import {
  checkPermissionsInLoader,
  useAppPermissions,
  useLocalStorage,
} from "@dmca/frontend/hooks";

export const Route = createFileRoute("/_authed/kanban/{-$id}")({
  component: RouteComponent,
  loader: async ({ context, params }) => {
    const { trpcQcProxy: trpc, qc } = context;
    await checkPermissionsInLoader("Kanban::ViewKanbanOverview", context);
    const kanbans = await qc.ensureQueryData(
      trpc.kanban.getWithOrder.queryOptions()
    );
    if (params.id && !kanbans.some((kanban) => kanban.id === params.id)) {
      throw redirect({
        to: "/kanban/{-$id}",
        params: { id: undefined },
        replace: true,
      });
    }
  },
  head: () => ({ meta: [{ title: "Kanban Overview | DNA Dashboard" }] }),
});

const ALL_PLANTS = "__all__";
const ALL_CATEGORIES = "__all__";

type ViewMode = "cards" | "table";
type SortField =
  | "name"
  | "category"
  | "requestCategories"
  | "currentStock"
  | "plantName"
  | "orderedOn"
  | "expectedFulfillment"
  | "completedOn";
type OrderState = "overdue" | "in-progress" | "none";

const TableSchema = object({
  id: string(),
  picture: optional(string()),
  name: string(),
  category: string(),
  departmentId: string(),
  requestCategories: string(),
  currentStock: number(),
  plantName: optional(string()),
  orderedOn: optional(luxonDTValiType()),
  expectedFulfillment: optional(luxonDTValiType()),
  completedOn: optional(luxonDTValiType()),
  orderPriority: number(),
});

type KanbanRow = {
  id: string;
  picture?: string;
  name: string;
  category: string;
  departmentId: string;
  requestCategories: string;
  currentStock: number;
  plantName?: string;
  orderedOn?: DateTime<true>;
  expectedFulfillment?: DateTime<true>;
  completedOn?: DateTime<true>;
  orderPriority: number;
};

function getOrderState(row: KanbanRow): OrderState {
  return row.orderPriority === 0
    ? "overdue"
    : row.orderPriority === 1
    ? "in-progress"
    : "none";
}

function orderColor(state: OrderState) {
  return state === "overdue"
    ? "error.main"
    : state === "in-progress"
    ? "primary.main"
    : "transparent";
}

function orderGlow(state: OrderState) {
  if (state === "none") return undefined;
  return (theme: Theme) =>
    `0 0 9px 2px ${
      theme.palette[state === "overdue" ? "error" : "primary"].main
    }`;
}

function orderStatusLabel(state: OrderState) {
  return state === "overdue"
    ? "Overdue order"
    : state === "in-progress"
    ? "Order in progress"
    : "No active order";
}

function ItemImage({
  item,
  size = 72,
}: {
  item: IKanbanWithOrder;
  size?: number;
}) {
  const [failedUrl, setFailedUrl] = useState<string>();
  return item.imageUrl && item.imageUrl !== failedUrl ? (
    <Box
      component="img"
      src={item.imageUrl}
      alt={item.name}
      loading="lazy"
      onError={() => setFailedUrl(item.imageUrl)}
      sx={{ width: size, height: size, borderRadius: 1, objectFit: "cover" }}
    />
  ) : (
    <Box
      aria-label="No image available"
      sx={{
        width: size,
        height: size,
        borderRadius: 1,
        bgcolor: "action.hover",
        display: "grid",
        placeItems: "center",
      }}
    >
      <ImageNotSupportedIcon color="disabled" />
    </Box>
  );
}

function StockDisplay({ item }: { item: IKanbanWithOrder }) {
  if (item.trackInventory === false)
    return <Typography>Not tracked</Typography>;
  const status = getKanbanStockStatus(
    item.currentStock,
    item.minStock,
    item.reorderThreshold
  );
  return (
    <Stack spacing={0.5} sx={{ minWidth: 170 }}>
      <Stack direction="row" justifyContent="space-between" gap={1}>
        <Typography>
          {item.currentStock} {item.unitOfMeasurement}
        </Typography>
        <Chip size="small" color={status.color} label={status.label} />
      </Stack>
      <LinearProgress
        variant="determinate"
        color={status.color}
        value={Math.min(
          100,
          (item.currentStock / Math.max(item.maxStock, 1)) * 100
        )}
        sx={{ height: 8, borderRadius: 1 }}
      />
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="caption">Min: {item.minStock}</Typography>
        <Typography variant="caption">Max: {item.maxStock}</Typography>
      </Stack>
    </Stack>
  );
}

function RouteComponent() {
  const { trpcQcProxy: trpc } = Route.useRouteContext();
  const trpcClient = useTRPCClient();
  const { id: kanbanId } = Route.useParams();
  const navigate = Route.useNavigate();
  const [now, setNow] = useState(() => DateTime.now());
  const { data: kanbans, isLoading } = useSuspenseQuery(
    trpc.kanban.getWithOrder.queryOptions(undefined, {
      staleTime: 60_000,
      refetchInterval: 60_000,
      refetchIntervalInBackground: true,
    })
  );
  const { data: departments = [] } = useQuery(
    trpc.kanban.getDepartments.queryOptions()
  );
  const departmentNames = useMemo(
    () => new Map(departments.map(({ id, name }) => [id, name])),
    [departments]
  );
  const imageKeys = useMemo(
    () =>
      kanbans
        .filter((item) => item.imageKey)
        .map((item) => [item.id, item.imageKey])
        .sort(([left], [right]) => left!.localeCompare(right!)),
    [kanbans]
  );
  const imageQuery = trpc.kanban.getImageUrls.queryOptions();
  const {
    data: imageUrls,
    isError: imageUrlsFailed,
    refetch: refetchImages,
  } = useQuery({
    ...imageQuery,
    // A changed image key needs a fresh URL, even while the old URL is cached.
    queryKey: [
      imageQuery.queryKey[0],
      { ...imageQuery.queryKey[1], input: imageKeys },
    ],
    // The key tracks image changes; the RPC still has no request input.
    queryFn: () => trpcClient.kanban.getImageUrls.query(),
    enabled: imageKeys.length > 0,
    staleTime: 240_000,
    refetchInterval: 240_000,
    refetchOnWindowFocus: true,
  });
  useEffect(() => {
    const interval = window.setInterval(() => setNow(DateTime.now()), 60_000);
    return () => window.clearInterval(interval);
  }, []);

  const hasPermission = useAppPermissions();
  const canViewStock = hasPermission("Kanban::ViewKanbanStock");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedPlant, setSelectedPlant] = useLocalStorage(
    "kanban-selected-plant",
    ALL_PLANTS
  );
  const [selectedCategory, setSelectedCategory] = useLocalStorage(
    "kanban-selected-category",
    ALL_CATEGORIES
  );
  useEffect(() => {
    if (
      departments.length &&
      selectedCategory !== ALL_CATEGORIES &&
      !departments.some(({ id }) => id === selectedCategory)
    )
      setSelectedCategory(ALL_CATEGORIES);
  }, [departments, selectedCategory, setSelectedCategory]);
  const [selectedRequestCategory, setSelectedRequestCategory] = useLocalStorage(
    "kanban-selected-request-category",
    ALL_CATEGORIES
  );
  const [view, setView] = useLocalStorage<ViewMode>("kanban-view", "cards");
  const [sortField, setSortField] = useLocalStorage<SortField>(
    "kanban-sort-field",
    "name"
  );
  const [sortDescending, setSortDescending] = useLocalStorage(
    "kanban-sort-descending",
    false
  );

  const itemById = useMemo(
    () =>
      new Map(
        kanbans.map((item) => [
          item.id,
          {
            ...item,
            imageUrl: item.imageKey ? imageUrls?.[item.id] : undefined,
          },
        ])
      ),
    [kanbans, imageUrls]
  );
  const rows = useMemo<KanbanRow[]>(
    () =>
      kanbans.map((item) => {
        const active = item.order?.status === "in-progress";
        const expectedFulfillment = active
          ? getKanbanExpectedDate(item.order!.createdAt, item.leadTimeDay)
          : undefined;
        return {
          id: item.id,
          picture: imageUrls?.[item.id],
          name: item.name,
          category: departmentNames.get(item.category) ?? item.category,
          departmentId: item.category,
          requestCategories: getKanbanRequestCategories(item)
            .map((category) => KanbanRequestCategoryNames[category])
            .join(", "),
          currentStock: item.currentStock,
          plantName: item.plantName,
          orderedOn: active
            ? (item.order!.createdAt.setZone(
                DateZone.instance
              ) as DateTime<true>)
            : undefined,
          expectedFulfillment,
          completedOn: item.lastCompletedAt?.setZone(DateZone.instance) as
            | DateTime<true>
            | undefined,
          orderPriority: active
            ? expectedFulfillment &&
              isKanbanOrderOverdue(expectedFulfillment, now)
              ? 0
              : 1
            : 2,
        };
      }),
    [kanbans, imageUrls, departmentNames, now]
  );

  const plantOptions = useMemo(
    () => [
      { name: "All Plants", value: ALL_PLANTS },
      ...Array.from(
        new Set(
          rows.flatMap((item) => (item.plantName ? [item.plantName] : []))
        )
      )
        .sort()
        .map((value) => ({ name: value, value })),
    ],
    [rows]
  );
  const categoryOptions = [
    { name: "All Departments", value: ALL_CATEGORIES },
    ...departments.map(({ id, name }) => ({ name, value: id })),
  ];
  const requestCategoryOptions = [
    { name: "All Order Types", value: ALL_CATEGORIES },
    ...Object.entries(KanbanRequestCategoryNames).map(([value, name]) => ({
      name,
      value,
    })),
  ];
  const sortOptions = [
    { name: "Name", value: "name" },
    { name: "Department", value: "category" },
    { name: "Order Type", value: "requestCategories" },
    ...(canViewStock ? [{ name: "Stock", value: "currentStock" }] : []),
    { name: "Plant", value: "plantName" },
    { name: "Ordered On", value: "orderedOn" },
    { name: "Expected Fulfillment", value: "expectedFulfillment" },
    { name: "Last Completed On", value: "completedOn" },
  ];
  const visibleRows = useMemo(() => {
    const term = search.trim().toLocaleLowerCase();
    const compareValue = (row: KanbanRow) => {
      const value = row[sortField];
      return DateTime.isDateTime(value) ? value.toMillis() : value ?? "";
    };
    return rows
      .filter((item) => {
        const source = itemById.get(item.id);
        return (
          (selectedPlant === ALL_PLANTS || item.plantName === selectedPlant) &&
          (selectedCategory === ALL_CATEGORIES ||
            item.departmentId === selectedCategory) &&
          (selectedRequestCategory === ALL_CATEGORIES ||
            item.requestCategories.includes(
              KanbanRequestCategoryNames[
                selectedRequestCategory as keyof typeof KanbanRequestCategoryNames
              ]
            )) &&
          (!term ||
            `${item.id} ${item.name} ${item.category} ${
              item.requestCategories
            } ${item.plantName ?? ""} ${
              source?.vendor.map(({ partNumber }) => partNumber).join(" ") ?? ""
            } ${
              source?.emailActions
                .map(({ vendorPartNumber }) => vendorPartNumber)
                .join(" ") ?? ""
            }`
              .toLocaleLowerCase()
              .includes(term))
        );
      })
      .sort((a, b) => {
        const priority = a.orderPriority - b.orderPriority;
        if (priority) return priority;
        const left = compareValue(a);
        const right = compareValue(b);
        const result =
          typeof left === "number" && typeof right === "number"
            ? left - right
            : String(left).localeCompare(String(right), undefined, {
                numeric: true,
              });
        return sortDescending ? -result : result;
      });
  }, [
    rows,
    itemById,
    search,
    selectedCategory,
    selectedRequestCategory,
    selectedPlant,
    sortDescending,
    sortField,
  ]);

  const { getReactTable } = useTable({
    data: visibleRows,
    schema: TableSchema,
    searchable: false,
    enableColumnFilter: false,
    enablePersistentSort: false,
    isLoading,
    cols: {
      allCols: { sortFunc: false },
      id: { show: false },
      departmentId: { show: false },
      orderPriority: { show: false },
      picture: {
        name: "Picture",
        renderFunc: (_, row) => (
          <ItemImage item={itemById.get(row.id)!} size={52} />
        ),
      },
      name: {
        name: "Name",
        renderFunc: (value, row) => {
          const statusLabel = orderStatusLabel(getOrderState(row));
          return (
            <Stack title={statusLabel}>
              <Typography>{value as string}</Typography>
              <Typography variant="caption" color="text.secondary">
                {row.id}
              </Typography>
              <Box
                component="span"
                sx={{
                  position: "absolute",
                  width: 1,
                  height: 1,
                  overflow: "hidden",
                  clip: "rect(0 0 0 0)",
                }}
              >
                {statusLabel}
              </Box>
            </Stack>
          );
        },
      },
      category: { name: "Department" },
      requestCategories: {
        name: "Order Type",
        renderFunc: (value) => {
          const categories = (value as string).split(", ").filter(Boolean);
          return categories.length
            ? categories.map((category, index) => (
                <div key={category}>
                  {category}
                  {index < categories.length - 1 ? "," : ""}
                </div>
              ))
            : "No order type";
        },
      },
      currentStock: {
        name: "Stock",
        show: canViewStock,
        renderFunc: (_, row) => <StockDisplay item={itemById.get(row.id)!} />,
      },
      plantName: { name: "Plant" },
      orderedOn: { name: "Ordered On" },
      expectedFulfillment: { name: "Expected Fulfillment" },
      completedOn: { name: "Last Completed On" },
    },
  });

  const openItem = (id: string) =>
    navigate({ to: "/kanban/{-$id}", params: { id }, replace: true });

  return (
    <Container maxWidth="xl">
      <PageHeader>Kanban Overview</PageHeader>
      {hasPermission("Kanban::UpdateKanbanDetails") && (
        <Button onClick={() => setShowCreateModal(true)}>
          Create New Kanban
        </Button>
      )}
      <Toolbar
        sx={{
          px: "0 !important",
          gap: 2,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <Input
          data-testid="generic-table-component-searchbar"
          label="Search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ maxWidth: 260 }}
        />
        <Select
          label="Plant"
          allowNull={false}
          value={selectedPlant}
          options={plantOptions}
          onChange={(event) => setSelectedPlant(event.target.value)}
          sx={{ maxWidth: 220 }}
        />
        <Select
          label="Department"
          allowNull={false}
          value={selectedCategory}
          options={categoryOptions}
          onChange={(event) => setSelectedCategory(event.target.value)}
          sx={{ maxWidth: 220 }}
        />
        <Select
          label="Order Type"
          allowNull={false}
          value={selectedRequestCategory}
          options={requestCategoryOptions}
          onChange={(event) => setSelectedRequestCategory(event.target.value)}
          sx={{ maxWidth: 220 }}
        />
        <Select
          label="Sort By"
          allowNull={false}
          value={sortField}
          options={sortOptions}
          onChange={(event) => setSortField(event.target.value as SortField)}
          sx={{ maxWidth: 220 }}
        />
        <Button
          variant="outlined"
          onClick={() => setSortDescending(!sortDescending)}
        >
          {sortDescending ? "Descending" : "Ascending"}
        </Button>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={view}
          onChange={(_, value: ViewMode | null) => value && setView(value)}
          aria-label="Kanban view"
        >
          <ToggleButton value="cards" aria-label="Cards view">
            <GridViewIcon />
          </ToggleButton>
          <ToggleButton value="table" aria-label="Table view">
            <TableRowsIcon />
          </ToggleButton>
        </ToggleButtonGroup>
      </Toolbar>

      {imageUrlsFailed && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Kanban images could not be loaded.{" "}
          <Button onClick={() => void refetchImages()}>Retry</Button>
        </Alert>
      )}

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="caption">Order:</Typography>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Box
            sx={{
              width: 16,
              height: 12,
              border: 2,
              borderColor: "error.main",
              boxShadow: (theme) =>
                `0 0 8px 1px ${theme.palette["error"].main}`,
            }}
          />
          <Typography variant="caption">Overdue</Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Box
            sx={{
              width: 16,
              height: 12,
              border: 2,
              borderColor: "primary.main",
              boxShadow: (theme) =>
                `0 0 8px 1px ${theme.palette["primary"].main}`,
            }}
          />
          <Typography variant="caption">In progress</Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          No outline: no active order
        </Typography>
      </Stack>

      {view === "cards" ? (
        <Box
          data-testid="kanban-card-grid"
          sx={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fill, minmax(min(100%, 340px), 1fr))",
            gap: 2,
            pb: 2,
          }}
        >
          {visibleRows.map((row) => {
            const item = itemById.get(row.id)!;
            const state = getOrderState(row);
            const statusLabel = orderStatusLabel(state);
            return (
              <Tooltip key={row.id} title={statusLabel} placement="top">
                <Card
                  variant="outlined"
                  sx={{
                    borderWidth: 2,
                    borderColor: orderColor(state),
                    boxShadow: orderGlow(state),
                    height: "100%",
                  }}
                >
                  <CardActionArea
                    onClick={() => openItem(row.id)}
                    aria-label={`${item.name}, ${statusLabel}`}
                    sx={{ p: 2, height: "100%" }}
                  >
                    <Stack spacing={2}>
                      <Stack direction="row" spacing={2}>
                        <ItemImage item={item} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="h6" noWrap>
                            {item.name}
                          </Typography>
                          <Typography color="text.secondary">
                            {item.id}
                          </Typography>
                          <Typography variant="body2">
                            {row.category}
                            {item.plantName ? ` · ${item.plantName}` : ""}
                          </Typography>
                          <Typography variant="body2">
                            {row.requestCategories || "No order type"}
                          </Typography>
                        </Box>
                      </Stack>
                      {canViewStock && <StockDisplay item={item} />}
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        gap={2}
                      >
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Ordered On
                          </Typography>
                          <Typography variant="body2">
                            {row.orderedOn?.toLocaleString(
                              DateTime.DATE_SHORT
                            ) ?? "—"}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Expected
                          </Typography>
                          <Typography variant="body2">
                            {row.expectedFulfillment?.toLocaleString(
                              DateTime.DATE_SHORT
                            ) ?? "—"}
                          </Typography>
                        </Box>

                        <Box sx={{ textAlign: "right" }}>
                          <Typography variant="caption" color="text.secondary">
                            Last completed
                          </Typography>
                          <Typography variant="body2">
                            {row.completedOn?.toLocaleString(
                              DateTime.DATE_SHORT
                            ) ?? "—"}
                          </Typography>
                        </Box>
                      </Stack>
                    </Stack>
                  </CardActionArea>
                </Card>
              </Tooltip>
            );
          })}
          {!visibleRows.length && (
            <Typography color="text.secondary">
              No Kanban items match these filters.
            </Typography>
          )}
        </Box>
      ) : (
        <Table
          getTable={getReactTable}
          sx={{
            overflowX: "clip",
            "& > .MuiBox-root": {
              px: 1,
              overflow: "clip",
              "& th, & td": { overflowWrap: "anywhere" },
            },
          }}
          getRowSx={(row) => {
            const state = getOrderState(row);
            return {
              outline: "2px solid",
              outlineColor: orderColor(state),
              outlineOffset: -2,
              boxShadow: orderGlow(state),
              position: state === "none" ? undefined : "relative",
              zIndex: state === "none" ? undefined : 1,
            };
          }}
          onRowClick={(row) => openItem(row.id)}
        />
      )}

      {kanbanId && (
        <UpdateKanbanModal
          open
          kanban={itemById.get(kanbanId)!}
          onClose={() =>
            navigate({
              to: "/kanban/{-$id}",
              params: { id: undefined },
              replace: true,
            })
          }
        />
      )}
      {showCreateModal && (
        <CreateKanbanModal open onClose={() => setShowCreateModal(false)} />
      )}
    </Container>
  );
}
